import { getImageProps } from 'next/image';

// `data-swatch-hover-preload` scopes the selector to links this module injected —
// `link[rel="preload"][as="image"]` alone would also match unrelated preload links
// elsewhere in the document (e.g. Next.js's own priority-image preloading), which
// would make the dedupe check above skip a genuinely new preload, and could evict
// someone else's `<link>` once the cap below is hit.
const PRELOAD_LINK_SELECTOR =
  'link[rel="preload"][as="image"][data-swatch-hover-preload="true"]';

/**
 * Caps how many preload links this module will leave sitting in `document.head`.
 * Client-side navigation doesn't reset `<head>`, so without a cap, hovering many
 * swatches across many products/pages in one session would grow it unbounded.
 * Eviction is FIFO (oldest preload dropped first) once the cap is hit.
 */
const MAX_PRELOAD_LINKS = 24;

/**
 * Preloads the exact resource a `next/image` with this `src`/`sizes` will
 * request, via a `<link rel="preload" as="image" imagesrcset imagesizes>`.
 * `next/image` rewrites `src` to its own optimizer endpoint keyed by `sizes`
 * (a different querystring per resolved width) — warming the raw `src` with
 * `new Image()` misses that cache key entirely. Resolving through
 * `getImageProps` with the target's own `sizes` targets the same resource the
 * real image will end up requesting.
 */
export function preloadResponsiveImage(url: string, sizes: string): void {
  if (typeof window === 'undefined') {
    return;
  }

  const { props } = getImageProps({
    src: url,
    alt: '',
    fill: true,
    sizes,
  });

  const existingLinks = Array.from(
    document.head.querySelectorAll<HTMLLinkElement>(PRELOAD_LINK_SELECTOR)
  );

  // Keyed on `href` + `imagesizes` rather than `href` alone: two callers can
  // resolve the same underlying image to the same optimizer `src` (Next.js's
  // `getWidths` picks the widths list — and therefore the fallback `src` —
  // off `deviceSizes`/`imageSizes`, not the literal `sizes` string) while
  // still needing a different `imagesizes`/`srcSet` pair, e.g. the same
  // product image hovered from an Algolia hit vs. a PLP product card. Keying
  // on `href` alone would skip the second preload and leave the browser with
  // the wrong `imagesizes` hint for that layout.
  const desiredImageSizes = props.sizes ?? null;
  if (
    existingLinks.some(
      (link) =>
        link.getAttribute('href') === props.src &&
        link.getAttribute('imagesizes') === desiredImageSizes
    )
  ) {
    return;
  }

  const link = document.createElement('link');
  link.setAttribute('rel', 'preload');
  link.setAttribute('as', 'image');
  link.setAttribute('data-swatch-hover-preload', 'true');
  link.setAttribute('href', props.src);
  if (props.srcSet) {
    link.setAttribute('imagesrcset', props.srcSet);
  }
  if (props.sizes) {
    link.setAttribute('imagesizes', props.sizes);
  }
  document.head.appendChild(link);

  const evictionCount = existingLinks.length + 1 - MAX_PRELOAD_LINKS;
  for (let i = 0; i < evictionCount; i++) {
    existingLinks[i]?.remove();
  }
}
